/**
 * WorkflowGuardNoticeProvider — feedback global para bloqueios do Workflow.
 *
 * Substitui o toast vermelho por um modal de advertência quando o backend
 * informa que a ação foi impedida por uma pendência do processo. A fila evita
 * empilhamento de modais em tentativas rápidas e a assinatura remove duplicados.
 *
 * RELEASE: 2026-09-03 v1.0.0 - Criação do provider/modal centralizado.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { ListChecks, ShieldAlert } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  getWorkflowGuardMessage,
  getWorkflowGuardPolicy,
  isWorkflowGuardError,
} from '@/lib/workflowGuardNotice';
import { cn } from '@/lib/utils';

interface WorkflowGuardNoticeOptions {
  /** Título contextual opcional; por padrão usa o título da política retornada */
  title?: string;
  /** Orientação curta opcional exibida abaixo da mensagem do processo */
  instruction?: string;
}

interface WorkflowGuardNoticeItem {
  id: number;
  signature: string;
  title: string;
  message: string;
  instruction: string;
}

interface WorkflowGuardNoticeContextValue {
  /**
   * Exibe o aviso quando o erro pertence ao Workflow.
   * Retorna `true` quando consumiu o erro, permitindo que o caller evite toast.
   */
  showWorkflowGuardNotice: (
    error: unknown,
    options?: WorkflowGuardNoticeOptions,
  ) => boolean;
}

const WorkflowGuardNoticeContext = createContext<WorkflowGuardNoticeContextValue | null>(null);

const ORIENTACAO_PADRAO =
  'Consulte as pendências do processo no resumo do registro antes de tentar novamente.';

export function WorkflowGuardNoticeProvider({ children }: { children: ReactNode }) {
  const [fila, setFila] = useState<WorkflowGuardNoticeItem[]>([]);
  const proximoIdRef = useRef(1);

  const showWorkflowGuardNotice = useCallback(
    (error: unknown, options?: WorkflowGuardNoticeOptions) => {
      if (!isWorkflowGuardError(error)) return false;

      const policy = getWorkflowGuardPolicy(error);
      const title = options?.title ?? (
        policy === 'WARN' ? 'Atenção do processo' : 'Ação bloqueada pelo processo'
      );
      const message = getWorkflowGuardMessage(error);
      const instruction = options?.instruction ?? ORIENTACAO_PADRAO;
      const signature = `${title}::${message}`;

      setFila((atual) => {
        // Deduplica tentativas repetidas que geram a mesma resposta do processo.
        if (atual.some((item) => item.signature === signature)) return atual;

        const aviso: WorkflowGuardNoticeItem = {
          id: proximoIdRef.current++,
          signature,
          title,
          message,
          instruction,
        };

        // Mantém no máximo 3 avisos para não criar uma fila longa em loops de UI.
        return [...atual, aviso].slice(-3);
      });

      return true;
    },
    [],
  );

  const fecharAtual = useCallback(() => {
    setFila((atual) => atual.slice(1));
  }, []);

  const value = useMemo(
    () => ({ showWorkflowGuardNotice }),
    [showWorkflowGuardNotice],
  );

  const avisoAtual = fila[0] ?? null;

  return (
    <WorkflowGuardNoticeContext.Provider value={value}>
      {children}

      <AlertDialog
        open={!!avisoAtual}
        onOpenChange={(open) => {
          if (!open) fecharAtual();
        }}
      >
        <AlertDialogPortal>
          {/*
           * z-[90] segue a escala oficial de modais aninhados: este aviso pode ser
           * aberto a partir do modal do negócio/contrato e precisa ficar acima dele.
           */}
          <AlertDialogOverlay className="z-[90]" />
          <AlertDialogPrimitive.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-[90] grid w-[calc(100%-2rem)] max-w-md',
              '-translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border',
              'bg-popover text-popover-foreground shadow-xl duration-200',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            )}
            // O fechamento fica concentrado no botão "Entendi" para leitura consciente.
            // A overlay bloqueia interação fora do aviso; o wrapper local não expõe onPointerDownOutside.
            onEscapeKeyDown={(event) => event.preventDefault()}
          >
            <AlertDialogHeader className="space-y-3 p-5 pb-4 text-left">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                  <ShieldAlert className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <AlertDialogTitle className="text-base font-semibold leading-6">
                    {avisoAtual?.title}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-sm leading-6 text-muted-foreground">
                    {avisoAtual?.message}
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>

            <div className="px-5 pb-4">
              <InlineNotice
                variant="warning"
                icon={ListChecks}
                className="border-l-2 px-0 py-0 text-xs leading-5"
              >
                {avisoAtual?.instruction}
              </InlineNotice>
            </div>

            <AlertDialogFooter className="border-t bg-muted/30 px-5 py-3 sm:justify-end">
              <AlertDialogAction className="h-9 px-4">
                Entendi
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPortal>
      </AlertDialog>
    </WorkflowGuardNoticeContext.Provider>
  );
}

/** Hook de acesso ao aviso global de Workflow. */
export function useWorkflowGuardNotice() {
  const context = useContext(WorkflowGuardNoticeContext);
  if (!context) {
    throw new Error('useWorkflowGuardNotice deve ser usado dentro de WorkflowGuardNoticeProvider');
  }
  return context;
}
